import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// AI usage + monthly budget API client (SaaS hardening). Mirrors the backend
// contract in backend/src/routes/aiUsage.ts.

export interface AiUsagePerModel {
  provider: string;
  model: string;
  /** BigInt serialized as string by the backend. */
  inputTokens: string;
  outputTokens: string;
  callCount: number;
  costEur: number;
}

/** Which slice of usage the per-model breakdown covers. */
export type AiUsagePeriod =
  | { kind: 'month'; month: string }
  | { kind: 'range'; from: string; to: string };

export interface AiUsageSummary {
  /** Current month ("YYYY-MM") in the business timezone — the budget card. */
  month: string;
  costEur: number;
  /** null = no limit configured. */
  budgetEur: number | null;
  /**
   * 'firmio' = the limit comes with the Firmio subscription (set in the Firmio
   * Console) and is read-only here. Absent on older backends.
   */
  budgetManagedBy?: 'firmio' | 'local';
  callCount: number;
  /** The breakdown's period — the current month unless one was requested. */
  period: AiUsagePeriod;
  periodCostEur: number;
  periodCallCount: number;
  /** Per-model rows inside `period`, most expensive first. */
  perModel: AiUsagePerModel[];
  /** Every month with recorded usage, newest first. */
  availableMonths: string[];
  /** First day with per-day data; null when none yet. Earlier days exist only as monthly totals. */
  dailySince: string | null;
}

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Query string for a breakdown period; the default (this month) sends nothing. */
export function aiUsagePeriodQuery(period?: AiUsagePeriod): string {
  if (!period) return '';
  const q = new URLSearchParams();
  if (period.kind === 'month') q.set('month', period.month);
  else {
    q.set('from', period.from);
    q.set('to', period.to);
  }
  return `?${q.toString()}`;
}

export async function getAiUsage(period?: AiUsagePeriod): Promise<AiUsageSummary> {
  const res = await apiFetch<Envelope<AiUsageSummary>>(
    `/api/ai-usage${aiUsagePeriodQuery(period)}`,
    { headers: authHeaders() },
  );
  return res.data;
}

/** Set (number) or clear (null) the monthly AI budget; returns the fresh summary. */
export async function saveAiBudget(monthlyLimitEur: number | null): Promise<AiUsageSummary> {
  const res = await apiFetch<Envelope<AiUsageSummary>>('/api/ai-usage/budget', {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify({ monthlyLimitEur }),
  });
  return res.data;
}
